import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { hasCapabilityOrProjectPm, CAP } from '../utils/permissions';
import ReactDOM from 'react-dom';
import {
    Box,
    Typography,
    CircularProgress,
    useTheme,
    alpha,
    FormControl,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField
} from '@mui/material';
import {
    MarkEmailRead as MarkEmailReadIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    HourglassEmpty as HourglassEmptyIcon,
    FileDownload as FileDownloadIcon,
    AttachFile as AttachFileIcon,
    Edit as EditIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Block as BlockIcon,
    ErrorOutline as ErrorOutlineIcon,
    Refresh as RefreshIcon,
    Close as CloseIcon,
    Check as CheckIcon,
    Cancel as CancelIcon,
    Email as EmailIcon,
    PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import { Transmittal, User, Project, EmailRecipientStatus, TransmittalEmailStatuses } from '../types';
import projectsService from '../services/projectsService';
import { authService } from '../services/authService';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ModuleRegistry, ClientSideRowModelModule, ValidationModule, QuickFilterModule, PaginationModule, RowAutoHeightModule, CellStyleModule, RowSelectionModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

// Register AG Grid modules
ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    ValidationModule,
    QuickFilterModule,
    PaginationModule,
    RowAutoHeightModule,
    CellStyleModule,
    RowSelectionModule
]);


// Nautilus Console Design System Constants
const NAUTILUS = {
    deepOcean: '#0f2134',
    sand: '#ccba9b',
    abyssal: '#0a1622',
};

interface TransmittalsTableProps {
    projectId: number;
}

// ─── Email Status Tooltip (Portal-based for AG Grid compatibility) ───────────

interface TooltipState {
    transmittal: Transmittal;
    anchorRect: DOMRect;
}

interface RecipientRowProps {
    recipient: EmailRecipientStatus;
    type: 'poc' | 'cc';
    transmittalId: number;
    projectId: number;
    isDark: boolean;
    onUpdated: (type: 'poc' | 'cc', id: number, newEmail: string) => void;
    onResent: (type: 'poc' | 'cc', id: number, newStatus: 'sent' | 'error', error?: string) => void;
}

const RecipientRow: React.FC<RecipientRowProps> = ({
    recipient, type, transmittalId, projectId, isDark, onUpdated, onResent
}) => {
    const [editing, setEditing] = useState(false);
    const [emailDraft, setEmailDraft] = useState(recipient.email);
    const [saving, setSaving] = useState(false);
    const [resending, setResending] = useState(false);

    const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
    const textPrimary = isDark ? '#ffffff' : NAUTILUS.deepOcean;
    const textSecondary = isDark ? '#b0bec5' : '#546e7a';

    const statusConfig: Record<string, { color: string; label: string; icon: React.ReactNode }> = {
        sent: { color: isDark ? '#66bb6a' : '#2e7d32', label: 'SENT', icon: <CheckCircleIcon sx={{ fontSize: 12 }} /> },
        pending: { color: textSecondary, label: 'PENDING', icon: <HourglassEmptyIcon sx={{ fontSize: 12 }} /> },
        error: { color: isDark ? '#ef5350' : '#d32f2f', label: 'ERROR', icon: <ErrorOutlineIcon sx={{ fontSize: 12 }} /> },
    };
    const cfg = statusConfig[recipient.status] || statusConfig.pending;

    const handleSaveEmail = async () => {
        if (!emailDraft.trim() || emailDraft === recipient.email) { setEditing(false); return; }
        setSaving(true);
        try {
            await projectsService.updateTransmittalRecipientEmail(projectId, transmittalId, type, recipient.id, emailDraft.trim());
            onUpdated(type, recipient.id, emailDraft.trim());
            setEditing(false);

            // Automatically resend after updating
            setResending(true);
            const pocIds = type === 'poc' ? [recipient.id] : [];
            const ccIds = type === 'cc' ? [recipient.id] : [];
            const res = await projectsService.resendTransmittalEmails(projectId, transmittalId, pocIds, ccIds);
            const resultList = type === 'poc' ? res.results.poc : res.results.cc;
            const myResult = resultList.find((r: any) => r.id === recipient.id);
            if (myResult) {
                onResent(type, recipient.id, myResult.status, myResult.error);
            }
            setResending(false);
        } catch (e) {
            console.error('Failed to update email or resend:', e);
            setResending(false);
        } finally {
            setSaving(false);
        }
    };

    const handleResend = async () => {
        setResending(true);
        try {
            const pocIds = type === 'poc' ? [recipient.id] : [];
            const ccIds = type === 'cc' ? [recipient.id] : [];
            const res = await projectsService.resendTransmittalEmails(projectId, transmittalId, pocIds, ccIds);
            const resultList = type === 'poc' ? res.results.poc : res.results.cc;
            const myResult = resultList.find((r: any) => r.id === recipient.id);
            if (myResult) {
                onResent(type, recipient.id, myResult.status, myResult.error);
            }
        } catch (e) {
            console.error('Failed to resend:', e);
        } finally {
            setResending(false);
        }
    };

    return (
        <div style={{
            padding: '8px 12px',
            borderBottom: `1px solid ${panelBorder}`,
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
        }}>
            {/* Name + status chip row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ color: textPrimary, fontSize: '0.8125rem', fontWeight: 500, flexShrink: 0 }}>
                    {recipient.name || '—'}
                </span>
                <span style={{
                    display: 'flex', alignItems: 'center', gap: '3px',
                    color: cfg.color, fontSize: '0.65rem', fontWeight: 700,
                    letterSpacing: '0.05em', whiteSpace: 'nowrap',
                }}>
                    {cfg.icon}{cfg.label}
                </span>
            </div>

            {/* Email row — editable inline */}
            {editing ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <input
                        autoFocus
                        value={emailDraft}
                        onChange={e => setEmailDraft(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSaveEmail(); if (e.key === 'Escape') setEditing(false); }}
                        style={{
                            flex: 1, fontSize: '0.75rem', fontFamily: 'monospace',
                            background: isDark ? alpha(NAUTILUS.abyssal, 0.8) : '#f5f5f5',
                            color: textPrimary, border: `1px solid ${NAUTILUS.sand}`,
                            borderRadius: '3px', padding: '2px 6px', outline: 'none',
                        }}
                    />
                    {saving ? (
                        <CircularProgress size={14} sx={{ color: NAUTILUS.sand }} />
                    ) : (
                        <>
                            <button onClick={handleSaveEmail} title="Save" style={{ background: 'none', border: 'none', cursor: 'pointer', color: isDark ? '#66bb6a' : '#2e7d32', padding: '2px', display: 'flex' }}>
                                <CheckIcon sx={{ fontSize: 14 }} />
                            </button>
                            <button onClick={() => { setEditing(false); setEmailDraft(recipient.email); }} title="Cancel" style={{ background: 'none', border: 'none', cursor: 'pointer', color: isDark ? '#ef5350' : '#d32f2f', padding: '2px', display: 'flex' }}>
                                <CancelIcon sx={{ fontSize: 14 }} />
                            </button>
                        </>
                    )}
                </div>
            ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <button
                        onClick={() => { setEmailDraft(recipient.email); setEditing(true); }}
                        title="Edit email address"
                        disabled={resending}
                        style={{
                            background: 'none', border: 'none', cursor: resending ? 'default' : 'pointer', padding: 0,
                            color: textSecondary, fontSize: '0.75rem', fontFamily: 'monospace',
                            textAlign: 'left', textDecoration: resending ? 'none' : 'underline dotted', textUnderlineOffset: '2px',
                            opacity: resending ? 0.7 : 1,
                        }}
                    >
                        {recipient.email}
                    </button>
                    {!resending && <EditIcon sx={{ fontSize: 11, color: alpha(textSecondary, 0.5) }} />}
                    {resending && <CircularProgress size={10} sx={{ color: textSecondary }} />}
                </div>
            )}

            {/* Error message */}
            {recipient.status === 'error' && recipient.error && (
                <span style={{ color: isDark ? '#ef5350' : '#d32f2f', fontSize: '0.7rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {recipient.error}
                </span>
            )}

            {/* Resend button — only for error status */}
            {recipient.status === 'error' && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2px' }}>
                    <button
                        onClick={handleResend}
                        disabled={resending}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            background: alpha(isDark ? '#ef5350' : '#d32f2f', 0.1),
                            border: `1px solid ${isDark ? '#ef5350' : '#d32f2f'}`,
                            borderRadius: '3px', cursor: resending ? 'default' : 'pointer',
                            color: isDark ? '#ef5350' : '#d32f2f',
                            fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em',
                            padding: '3px 8px',
                        }}
                    >
                        {resending ? <CircularProgress size={10} sx={{ color: 'inherit' }} /> : <RefreshIcon sx={{ fontSize: 12 }} />}
                        RESEND
                    </button>
                </div>
            )}
        </div>
    );
};

interface EmailTooltipPanelProps {
    transmittal: Transmittal;
    anchorRect: DOMRect;
    projectId: number;
    isDark: boolean;
    onClose: () => void;
    onTransmittalUpdate: (id: number, statuses: TransmittalEmailStatuses) => void;
}

const EmailTooltipPanel: React.FC<EmailTooltipPanelProps> = ({
    transmittal, anchorRect, projectId, isDark, onClose, onTransmittalUpdate
}) => {
    const [statuses, setStatuses] = useState<TransmittalEmailStatuses>(
        transmittal.email_statuses || { poc: [], cc: [] }
    );
    const panelRef = useRef<HTMLDivElement>(null);

    // Position below or above the anchor
    const PANEL_WIDTH = 360;
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    let left = Math.min(anchorRect.left, viewportW - PANEL_WIDTH - 12);
    let top = anchorRect.bottom + 6;
    if (top + 400 > viewportH) {
        top = anchorRect.top - 6 - Math.min(400, viewportH * 0.6);
    }

    const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';
    const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
    const textPrimary = isDark ? '#ffffff' : NAUTILUS.deepOcean;
    const textSecondary = isDark ? '#b0bec5' : '#546e7a';
    const headerBg = isDark ? alpha(NAUTILUS.abyssal, 0.95) : alpha('#f5f5f5', 0.98);

    const hasPoc = statuses.poc.length > 0;
    const hasCc = statuses.cc.length > 0;

    const handleUpdated = (type: 'poc' | 'cc', id: number, newEmail: string) => {
        setStatuses(prev => {
            const updated = {
                ...prev,
                [type]: prev[type].map(r =>
                    r.id === id ? { ...r, email: newEmail, status: 'pending' as const, error: null } : r
                )
            };
            onTransmittalUpdate(transmittal.id, updated);
            return updated;
        });
    };

    const handleResent = (type: 'poc' | 'cc', id: number, newStatus: 'sent' | 'error', error?: string) => {
        setStatuses(prev => {
            const updated = {
                ...prev,
                [type]: prev[type].map(r =>
                    r.id === id ? { ...r, status: newStatus, error: error || null } : r
                )
            };
            onTransmittalUpdate(transmittal.id, updated);
            return updated;
        });
    };

    // Close on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    return ReactDOM.createPortal(
        <div
            ref={panelRef}
            style={{
                position: 'fixed',
                top, left,
                width: PANEL_WIDTH,
                maxHeight: '60vh',
                overflowY: 'auto',
                zIndex: 99999,
                background: surfaceBg,
                border: `1px solid ${panelBorder}`,
                borderRadius: '6px',
                boxShadow: isDark
                    ? '0 8px 32px rgba(0,0,0,0.6)'
                    : '0 8px 24px rgba(15,33,52,0.18)',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            }}
        >
            {/* Header */}
            <div style={{
                background: headerBg,
                borderBottom: `1px solid ${panelBorder}`,
                padding: '10px 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'sticky',
                top: 0,
                zIndex: 1,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <EmailIcon sx={{ fontSize: 14, color: NAUTILUS.sand }} />
                    <span style={{
                        color: textPrimary, fontSize: '0.75rem', fontWeight: 700,
                        letterSpacing: '0.05em', textTransform: 'uppercase',
                    }}>
                        Email Delivery Status
                    </span>
                </div>
                <button
                    onClick={onClose}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: textSecondary, padding: '2px', display: 'flex' }}
                >
                    <CloseIcon sx={{ fontSize: 16 }} />
                </button>
            </div>

            {/* POC section */}
            {hasPoc && (
                <div>
                    <div style={{
                        padding: '6px 12px 4px',
                        background: isDark ? alpha(NAUTILUS.sand, 0.06) : alpha(NAUTILUS.sand, 0.08),
                        borderBottom: `1px solid ${panelBorder}`,
                    }}>
                        <span style={{ color: textSecondary, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                            POC / Main Recipients
                        </span>
                    </div>
                    {statuses.poc.map(r => (
                        <RecipientRow
                            key={r.id}
                            recipient={r}
                            type="poc"
                            transmittalId={transmittal.id}
                            projectId={projectId}
                            isDark={isDark}
                            onUpdated={handleUpdated}
                            onResent={handleResent}
                        />
                    ))}
                </div>
            )}

            {/* CC section */}
            {hasCc && (
                <div>
                    <div style={{
                        padding: '6px 12px 4px',
                        background: isDark ? alpha('#4a6a8a', 0.1) : alpha('#e8f0f8', 0.8),
                        borderTop: hasPoc ? `1px solid ${panelBorder}` : undefined,
                        borderBottom: `1px solid ${panelBorder}`,
                    }}>
                        <span style={{ color: textSecondary, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                            CC Recipients
                        </span>
                    </div>
                    {statuses.cc.map(r => (
                        <RecipientRow
                            key={r.id}
                            recipient={r}
                            type="cc"
                            transmittalId={transmittal.id}
                            projectId={projectId}
                            isDark={isDark}
                            onUpdated={handleUpdated}
                            onResent={handleResent}
                        />
                    ))}
                </div>
            )}

            {!hasPoc && !hasCc && (
                <div style={{ padding: '16px 14px', color: textSecondary, fontSize: '0.8125rem', textAlign: 'center' }}>
                    No recipient data available.
                </div>
            )}

            {/* Hint */}
            <div style={{ padding: '6px 12px', borderTop: `1px solid ${panelBorder}` }}>
                <span style={{ color: textSecondary, fontSize: '0.65rem', fontStyle: 'italic' }}>
                    Click an email address to edit it. Resend is only available for errors.
                </span>
            </div>
        </div>,
        document.body
    );
};

// ─── Aggregate status icon for the recipient cell ────────────────────────────

function formatAcknowledgementHover(iso: string): string {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return iso;
    const formatted = parsed.toLocaleString('en-GB', {
        timeZone: 'UTC',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    return `${formatted} UTC`;
}

function getAggregateStatus(statuses: TransmittalEmailStatuses | undefined): 'sent' | 'pending' | 'error' | 'none' {
    if (!statuses) return 'none';
    const all = [...statuses.poc, ...statuses.cc];
    if (all.length === 0) return 'none';
    if (all.some(r => r.status === 'error')) return 'error';
    if (all.some(r => r.status === 'pending')) return 'pending';
    return 'sent';
}

// ─── Main Component ──────────────────────────────────────────────────────────

const TransmittalsTable: React.FC<TransmittalsTableProps> = ({ projectId }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Design System Tokens
    const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
    const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';
    const sandAccent = NAUTILUS.sand;
    const textPrimary = isDark ? '#ffffff' : NAUTILUS.deepOcean;
    const textSecondary = isDark ? '#b0bec5' : '#546e7a';

    const [transmittals, setTransmittals] = useState<Transmittal[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [project, setProject] = useState<Project | null>(null);

    // Tooltip state
    const [tooltip, setTooltip] = useState<TooltipState | null>(null);

    // Comment Dialog State
    const [commentDialogOpen, setCommentDialogOpen] = useState(false);
    const [selectedTransmittal, setSelectedTransmittal] = useState<Transmittal | null>(null);
    const [commentText, setCommentText] = useState('');
    const [savingComment, setSavingComment] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [transmittalsData, projectData, userResponse] = await Promise.all([
                projectsService.getProjectTransmittals(projectId),
                projectsService.getProjectDetails(projectId),
                authService.getProfile()
            ]);

            setTransmittals(transmittalsData);
            setProject(projectData.project);
            if (userResponse.data?.user) {
                setCurrentUser(userResponse.data.user);
            }
            setError(null);
        } catch (err) {
            console.error('Failed to load data', err);
            setError('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        if (projectId) {
            fetchData();
        }
    }, [projectId, fetchData]);

    const canEdit = useMemo(() => {
        if (!currentUser || !project) return false;
        return hasCapabilityOrProjectPm(currentUser, CAP.MANAGE_TRANSMITTALS, project.pm_id);
    }, [currentUser, project]);

    const handleStatusChange = async (transmittalId: number, newStatus: string) => {
        try {
            await projectsService.updateTransmittalStatus(projectId, transmittalId, newStatus);
            setTransmittals(prev => prev.map(t =>
                t.id === transmittalId ? { ...t, status: newStatus } : t
            ));
        } catch (error) {
            console.error('Failed to update status', error);
        }
    };

    const handleOpenCommentDialog = (transmittal: Transmittal) => {
        setSelectedTransmittal(transmittal);
        setCommentText(transmittal.internal_comment || '');
        setCommentDialogOpen(true);
    };

    const handleCloseCommentDialog = () => {
        setCommentDialogOpen(false);
        setSelectedTransmittal(null);
        setCommentText('');
    };

    const handleSaveComment = async () => {
        if (!selectedTransmittal) return;
        setSavingComment(true);
        try {
            if (commentText.trim()) {
                await projectsService.setTransmittalComment(projectId, selectedTransmittal.id, commentText);
            } else {
                await projectsService.deleteTransmittalComment(projectId, selectedTransmittal.id);
            }
            await fetchData();
            handleCloseCommentDialog();
        } catch (error) {
            console.error('Failed to save comment', error);
        } finally {
            setSavingComment(false);
        }
    };

    const handleDeleteComment = async (transmittal: Transmittal) => {
        if (!window.confirm('Are you sure you want to delete this comment?')) return;
        try {
            await projectsService.deleteTransmittalComment(projectId, transmittal.id);
            await fetchData();
        } catch (error) {
            console.error('Failed to delete comment', error);
        }
    };

    const handleDeactivateTransmittal = async (transmittal: Transmittal) => {
        if (!window.confirm('Are you sure you want to instantly deactivate this transmittal link?')) return;
        try {
            await projectsService.deactivateTransmittal(projectId, transmittal.id);
            await fetchData();
        } catch (error) {
            console.error('Failed to deactivate transmittal', error);
        }
    };

    const handleDownloadReceptionPdf = async (transmittal: Transmittal) => {
        try {
            await projectsService.downloadTransmittalReceptionVerification(
                projectId,
                transmittal.id,
                transmittal.transmittal_number,
            );
        } catch (error) {
            console.error('Failed to download reception verification', error);
        }
    };

    // Update local email_statuses state after tooltip edits/resends (no full reload needed)
    const handleTransmittalUpdate = useCallback((id: number, statuses: TransmittalEmailStatuses) => {
        setTransmittals(prev => prev.map(t =>
            t.id === id ? { ...t, email_statuses: statuses } : t
        ));
    }, []);

    const StatusRenderer = (params: any) => {
        const transmittal = params.data as Transmittal;
        const status = transmittal.status?.toLowerCase() || 'sent';

        let statusConfig = {
            icon: <HourglassEmptyIcon sx={{ fontSize: 16 }} />,
            color: textSecondary,
            label: 'PENDING'
        };

        if (status === 'acknowledged' || transmittal.status === 'ACKNOWLEDGED') {
            statusConfig = {
                icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
                color: isDark ? '#66bb6a' : '#2e7d32',
                label: 'ACKNOWLEDGED'
            };
        } else if (status === 'expired' || (transmittal.expires_at && new Date(transmittal.expires_at) < new Date() && status !== 'acknowledged')) {
            statusConfig = {
                icon: <WarningIcon sx={{ fontSize: 16 }} />,
                color: isDark ? '#ff7043' : '#d84315',
                label: 'EXPIRED'
            };
        } else if (status === 'not received') {
            statusConfig = {
                icon: <WarningIcon sx={{ fontSize: 16 }} />,
                color: isDark ? '#ef5350' : '#d32f2f',
                label: 'NOT RECEIVED'
            };
        } else {
            statusConfig = {
                icon: <MarkEmailReadIcon sx={{ fontSize: 16 }} />,
                color: sandAccent,
                label: 'SENT'
            };
        }

        const isAcknowledged = status === 'acknowledged' || transmittal.status === 'ACKNOWLEDGED';
        const ackTitle = isAcknowledged
            ? (transmittal.acknowledgement_date
                ? `Acknowledged ${formatAcknowledgementHover(transmittal.acknowledgement_date)}`
                : 'Acknowledged')
            : undefined;

        if (canEdit && status !== 'acknowledged') {
            return (
                <div style={{ display: 'flex', alignItems: 'center', height: '100%', marginTop: '-4px' }}>
                    <select
                        value={status === 'expired' ? 'sent' : status}
                        onChange={(e) => handleStatusChange(transmittal.id, e.target.value)}
                        style={{
                            color: statusConfig.color,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            letterSpacing: '0.05em',
                            backgroundColor: 'transparent',
                            border: 'none',
                            outline: 'none',
                            cursor: 'pointer',
                            padding: '4px 0',
                            fontFamily: 'inherit',
                            appearance: 'auto'
                        }}
                    >
                        <option value="sent" style={{ color: sandAccent, backgroundColor: surfaceBg }}>SENT</option>
                        <option value="not received" style={{ color: isDark ? '#ef5350' : '#d32f2f', backgroundColor: surfaceBg }}>NOT RECEIVED</option>
                    </select>
                </div>
            );
        }

        return (
            <div
                title={ackTitle}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '100%', cursor: ackTitle ? 'help' : 'default' }}
            >
                <span style={{ color: statusConfig.color, display: 'flex' }}>
                    {statusConfig.icon}
                </span>
                <span style={{ color: statusConfig.color, fontWeight: 600, letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                    {statusConfig.label}
                </span>
            </div>
        );
    };

    const DocumentRenderer = (params: any) => {
        const t = params.data as Transmittal;
        return (
            <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column' }}>
                <span style={{ color: textPrimary, fontWeight: 500, fontFamily: 'monospace', fontSize: '0.8125rem', lineHeight: 1.2 }}>
                    {t.document_number}
                </span>
                <span style={{ color: textSecondary, display: 'block', lineHeight: 1.2, marginTop: '4px', whiteSpace: 'normal', wordBreak: 'break-word', fontSize: '0.75rem' }}>
                    {t.document_title}
                </span>
            </div>
        );
    };

    const RecipientRenderer = (params: any) => {
        const t = params.data as Transmittal;
        const recipients = t.main_recipients?.length
            ? t.main_recipients
            : [{ name: t.main_recipient_name, email: t.main_recipient_email }];

        const aggStatus = getAggregateStatus(t.email_statuses);
        const hasStatuses = aggStatus !== 'none';

        const statusIconColor: Record<string, string> = {
            sent: isDark ? '#66bb6a' : '#2e7d32',
            pending: textSecondary,
            error: isDark ? '#ef5350' : '#d32f2f',
        };

        const StatusIcon = () => {
            if (!hasStatuses) return null;
            if (aggStatus === 'error') return <ErrorOutlineIcon sx={{ fontSize: 14, color: statusIconColor.error }} />;
            if (aggStatus === 'pending') return <HourglassEmptyIcon sx={{ fontSize: 14, color: statusIconColor.pending }} />;
            return <CheckCircleIcon sx={{ fontSize: 14, color: statusIconColor.sent }} />;
        };

        return (
            <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {recipients.map((r, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ color: textPrimary, lineHeight: 1.2, fontSize: '0.8125rem' }}>
                            {r.name || r.email}
                        </span>
                        {r.name && (
                            <span style={{ color: textSecondary, fontFamily: 'monospace', lineHeight: 1.2, fontSize: '0.75rem' }}>
                                {r.email}
                            </span>
                        )}
                    </div>
                ))}

                {/* Email status trigger button */}
                {hasStatuses && canEdit && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setTooltip(prev =>
                                prev?.transmittal.id === t.id ? null : { transmittal: t, anchorRect: rect }
                            );
                        }}
                        title="View email delivery status"
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            background: aggStatus === 'error'
                                ? alpha(isDark ? '#ef5350' : '#d32f2f', 0.1)
                                : alpha(NAUTILUS.sand, 0.1),
                            border: `1px solid ${aggStatus === 'error'
                                ? (isDark ? alpha('#ef5350', 0.4) : alpha('#d32f2f', 0.3))
                                : alpha(NAUTILUS.sand, 0.3)}`,
                            borderRadius: '3px', cursor: 'pointer',
                            padding: '2px 6px', marginTop: '2px',
                            width: 'fit-content',
                        }}
                    >
                        <StatusIcon />
                        <span style={{
                            fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            color: aggStatus === 'error' ? (isDark ? '#ef5350' : '#d32f2f') : NAUTILUS.sand,
                        }}>
                            {aggStatus === 'error' ? 'EMAIL ERROR' : aggStatus === 'pending' ? 'EMAIL PENDING' : 'EMAILS SENT'}
                        </span>
                    </button>
                )}
            </div>
        );
    };


    const RevisionRenderer = (params: any) => (
        <div style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <div style={{ display: 'inline-block', padding: '2px 8px', border: `1px solid ${alpha(panelBorder, 0.8)}`, borderRadius: '4px', backgroundColor: isDark ? alpha('#000', 0.2) : alpha('#fff', 0.5) }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 600, color: sandAccent, fontSize: '0.75rem' }}>
                    {params.value}
                </span>
            </div>
        </div>
    );

    const FilesRenderer = (params: any) => {
        const t = params.data as Transmittal;
        return (
            <div style={{ display: 'flex', alignItems: 'center', height: '100%', gap: '8px' }}>
                <div title={`${t.files_count || 0} files`} style={{ display: 'flex', alignItems: 'center', color: textSecondary, cursor: 'help' }}>
                    <AttachFileIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{t.files_count || 0}</span>
                </div>
                {!!t.downloads_count && (
                    <div title={`${t.downloads_count} downloads`} style={{ display: 'flex', alignItems: 'center', color: sandAccent, marginLeft: '8px', cursor: 'help' }}>
                        <FileDownloadIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{t.downloads_count}</span>
                    </div>
                )}
            </div>
        );
    };

    const CommentRenderer = (params: any) => {
        const comment = params.value;
        if (!comment) return null;
        return (
            <div style={{ padding: '8px 0' }}>
                <span style={{ color: textPrimary, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8125rem', display: 'block' }}>
                    {comment}
                </span>
            </div>
        );
    };

    const ActionsRenderer = (params: any) => {
        const t = params.data as Transmittal;
        const hasComment = !!t.internal_comment;
        const isAcknowledged = t.status?.toLowerCase() === 'acknowledged';
        const isActive = t.status?.toLowerCase() === 'sent' &&
            (!t.expires_at || new Date(t.expires_at) >= new Date());

        if (!canEdit && !isAcknowledged) return null;

        return (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', gap: '4px' }}>
                {isAcknowledged && (
                    <button
                        title="Download reception verification PDF"
                        onClick={() => handleDownloadReceptionPdf(t)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: sandAccent, padding: '4px', display: 'flex' }}
                    >
                        <PictureAsPdfIcon fontSize="small" />
                    </button>
                )}
                {canEdit && (
                    <>
                <button
                    title={hasComment ? "Edit Comment" : "Add Comment"}
                    onClick={() => handleOpenCommentDialog(t)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: sandAccent, padding: '4px', display: 'flex' }}
                >
                    {hasComment ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                </button>
                {hasComment && (
                    <button
                        title="Delete Comment"
                        onClick={() => handleDeleteComment(t)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.palette.error.main, padding: '4px', display: 'flex' }}
                    >
                        <DeleteIcon fontSize="small" />
                    </button>
                )}
                {isActive && (
                    <button
                        title="Deactivate Link"
                        onClick={() => handleDeactivateTransmittal(t)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.palette.warning.main, padding: '4px', display: 'flex' }}
                    >
                        <BlockIcon fontSize="small" />
                    </button>
                )}
                    </>
                )}
            </div>
        );
    };

    // Strip time from sent_at so AG Grid's date filter never sees a time component
    const transmittalsForGrid = useMemo(() =>
        transmittals.map(t => ({
            ...t,
            sent_at: t.sent_at ? t.sent_at.split('T')[0] : t.sent_at
        })), [transmittals]);

    const dateFilterParams = useMemo(() => ({
        browserDatePicker: true,
        suppressAndOrCondition: true,
        comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
            if (cellValue == null) return -1;
            const [year, month, day] = cellValue.split('-').map(Number);
            const cellDate = new Date(year, month - 1, day, 0, 0, 0, 0);
            if (cellDate.getTime() === filterLocalDateAtMidnight.getTime()) return 0;
            return cellDate < filterLocalDateAtMidnight ? -1 : 1;
        }
    }), []);

    const columnDefs: ColDef<Transmittal>[] = useMemo(() => [
        {
            field: 'transmittal_number',
            headerName: 'TTL NO.',
            width: 230,
            filter: 'agTextColumnFilter',
            cellStyle: { fontFamily: 'monospace', fontSize: '0.75rem', display: 'flex', alignItems: 'center', color: 'inherit' }
        },
        {
            field: 'sent_at',
            headerName: 'SENT DATE',
            width: 130,
            valueFormatter: (params) => {
                if (!params.value) return '';
                const [year, month, day] = (params.value as string).split('-').map(Number);
                return new Date(year, month - 1, day).toLocaleDateString();
            },
            filter: 'agDateColumnFilter',
            filterParams: dateFilterParams,
            cellStyle: { fontFamily: 'monospace', fontSize: '0.8125rem', display: 'flex', alignItems: 'center' }
        },
        {
            field: 'document_number',
            headerName: 'DOCUMENT',
            flex: 1.5,
            minWidth: 200,
            autoHeight: true,
            filter: 'agTextColumnFilter',
            cellRenderer: DocumentRenderer,
            getQuickFilterText: (params) => `${params.data.document_number} ${params.data.document_title}`
        },
        {
            field: 'revision',
            headerName: 'REV',
            width: 80,
            filter: 'agTextColumnFilter',
            cellRenderer: RevisionRenderer
        },
        {
            field: 'main_recipient_name',
            headerName: 'RECIPIENT',
            flex: 1,
            minWidth: 180,
            autoHeight: true,
            filter: 'agTextColumnFilter',
            cellRenderer: RecipientRenderer,
            getQuickFilterText: (params) => `${params.data.main_recipient_name} ${params.data.main_recipient_email}`
        },
        {
            field: 'status',
            headerName: 'STATUS',
            width: 140,
            filter: 'agTextColumnFilter',
            cellRenderer: StatusRenderer
        },
        {
            field: 'internal_comment',
            headerName: 'COMMENT',
            flex: 1.5,
            minWidth: 200,
            autoHeight: true,
            filter: 'agTextColumnFilter',
            cellRenderer: CommentRenderer
        },
        {
            headerName: 'FILES',
            width: 110,
            cellRenderer: FilesRenderer
        },
        {
            headerName: '',
            width: 120,
            cellRenderer: ActionsRenderer,
            sortable: false,
            filter: false,
            hide: !canEdit && !transmittals.some(t => (t.status || '').toLowerCase() === 'acknowledged')
        }
    ], [canEdit, theme.palette.error.main, transmittals]);

    const defaultColDef = useMemo<ColDef>(() => ({
        sortable: true,
        resizable: true,
        suppressMovable: true,
        menuTabs: ['filterMenuTab'],
        floatingFilter: true
    }), []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={8}>
                <CircularProgress sx={{ color: sandAccent }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={4} display="flex" justifyContent="center">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
                className={isDark ? "ag-theme-quartz-dark" : "ag-theme-quartz"}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    width: '100%',
                    border: `1px solid ${panelBorder}`,
                    borderRadius: 1,
                    overflow: 'hidden'
                }}
            >
                <AgGridReact<Transmittal>
                    theme="legacy"
                    rowData={transmittalsForGrid}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    animateRows={true}
                    rowSelection={{ mode: 'multiRow', enableClickSelection: false }}
                    pagination={true}
                    paginationPageSize={15}
                    paginationPageSizeSelector={false}
                    domLayout='normal'
                    overlayNoRowsTemplate="<span style='padding: 10px; border: 1px dashed; opacity: 0.5'>No transmittals found.</span>"
                />
            </Box>

            {/* Email status tooltip portal */}
            {tooltip && (
                <EmailTooltipPanel
                    transmittal={tooltip.transmittal}
                    anchorRect={tooltip.anchorRect}
                    projectId={projectId}
                    isDark={isDark}
                    onClose={() => setTooltip(null)}
                    onTransmittalUpdate={handleTransmittalUpdate}
                />
            )}

            {/* Comment Dialog */}
            <Dialog
                open={commentDialogOpen}
                onClose={handleCloseCommentDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: isDark ? NAUTILUS.abyssal : '#fff',
                        border: `1px solid ${panelBorder}`,
                        backgroundImage: 'none'
                    }
                }}
            >
                <DialogTitle sx={{ color: textPrimary, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EditIcon fontSize="small" sx={{ color: sandAccent }} />
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                        {selectedTransmittal?.internal_comment ? 'Edit Comment' : 'Add Comment'}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="caption" sx={{ color: textSecondary, mb: 2, display: 'block' }}>
                        For transmittal: {selectedTransmittal?.document_number} {selectedTransmittal?.revision} to {selectedTransmittal?.main_recipient_name}
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="comment"
                        label="Internal Comment"
                        type="text"
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder="Add a comment only visible to internal staff..."
                        sx={{
                            mt: 1,
                            '& .MuiOutlinedInput-root': {
                                color: textPrimary,
                                '& fieldset': { borderColor: panelBorder },
                                '&:hover fieldset': { borderColor: sandAccent },
                                '&.Mui-focused fieldset': { borderColor: sandAccent },
                            },
                            '& .MuiInputLabel-root': { color: textSecondary },
                            '& .MuiInputLabel-root.Mui-focused': { color: sandAccent },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={handleCloseCommentDialog} sx={{ color: textSecondary }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveComment}
                        variant="contained"
                        disabled={savingComment}
                        sx={{
                            bgcolor: sandAccent,
                            color: NAUTILUS.deepOcean,
                            fontWeight: 600,
                            '&:hover': { bgcolor: alpha(sandAccent, 0.9) },
                            '&.Mui-disabled': { bgcolor: alpha(sandAccent, 0.3) }
                        }}
                    >
                        {savingComment ? <CircularProgress size={24} sx={{ color: NAUTILUS.deepOcean }} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TransmittalsTable;
